import { useEffect } from 'react';
import { Button } from '@/components/ui/Button';
import type { ResponseQuote } from '@/types';
import { cn } from '@/lib/utils';

interface ResponseQuoteCardProps {
  quote: ResponseQuote;
  onContinue: () => void;
}

// Extract emoji from text (if present at the start)
const extractEmoji = (text: string): { emoji: string | null; cleanText: string } => {
  const emojiRegex = /^(\p{Emoji_Presentation}|\p{Emoji}\uFE0F|\p{Emoji_Modifier_Base}\p{Emoji_Modifier}?|\u{1F3FB}-\u{1F3FF}|\u{1F9B0}-\u{1F9B3}|\u{200D}|\u{FE0F})+/u;
  const match = text.match(emojiRegex);
  
  if (match && match[0]) {
    return {
      emoji: match[0].trim(),
      cleanText: text.replace(emojiRegex, '').trim(),
    };
  }
  
  return { emoji: null, cleanText: text };
};

const quoteStyles: Record<
  NonNullable<ResponseQuote['style']>,
  { 
    gradient: string; 
    text: string; 
    badge: string;
    emojiBg: string;
  }
> = {
  info: {
    gradient: 'from-primary/20 via-primary/10 to-primary/5',
    text: 'text-foreground',
    badge: 'bg-primary/20 text-primary-foreground border-primary/30',
    emojiBg: 'bg-primary/10',
  },
  success: {
    gradient: 'from-emerald-500/20 via-emerald-400/10 to-emerald-300/5',
    text: 'text-foreground',
    badge: 'bg-emerald-500/20 text-emerald-foreground border-emerald-400/30',
    emojiBg: 'bg-emerald-500/10',
  },
  warning: {
    gradient: 'from-amber-500/20 via-amber-400/10 to-amber-300/5',
    text: 'text-foreground',
    badge: 'bg-amber-500/20 text-amber-foreground border-amber-400/30',
    emojiBg: 'bg-amber-500/10',
  },
  testimonial: {
    gradient: 'from-secondary/20 via-secondary/10 to-secondary/5',
    text: 'text-foreground',
    badge: 'bg-secondary/20 text-secondary-foreground border-secondary/30',
    emojiBg: 'bg-secondary/10',
  },
};

export const ResponseQuoteCard = ({ quote, onContinue }: ResponseQuoteCardProps) => {
  const style =
    (quote.style && quoteStyles[quote.style]) || quoteStyles.info;

  // Extract emoji from heading quote or quote text
  const headingText = quote.headingQuote || quote.quoteText || '';
  const { emoji, cleanText } = extractEmoji(headingText);
  const displayEmoji = emoji || '✨'; // Default emoji if none found

  // Prevent body scroll when overlay is shown
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in">
      {/* Backdrop with blur */}
      <div 
        className={cn(
          'absolute inset-0 bg-gradient-to-br',
          style.gradient,
          'backdrop-blur-md'
        )}
        onClick={onContinue}
        aria-hidden="true"
      />
      
      {/* Content Card */}
      <div className="relative w-full max-w-2xl mx-auto animate-scale-in">
        <div className={cn(
          'relative rounded-3xl border border-border/50 bg-card/95 backdrop-blur-xl',
          'shadow-2xl overflow-hidden',
          'p-8 sm:p-12 space-y-8'
        )}>
          {/* Decorative gradient overlays */}
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(120,119,198,0.1),transparent_50%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(167,139,250,0.1),transparent_50%)]" />
          
          {/* Content */}
          <div className="relative space-y-6 text-center">
            {/* Emoji Display */}
            {displayEmoji && (
              <div className={cn(
                'inline-flex items-center justify-center w-20 h-20 sm:w-24 sm:h-24 rounded-2xl',
                style.emojiBg,
                'border border-border/50 shadow-lg',
                'animate-scale-in'
              )}>
                <span 
                  className="text-4xl sm:text-5xl leading-none select-none"
                  role="img"
                  aria-hidden="true"
                >
                  {displayEmoji}
                </span>
              </div>
            )}

            {/* Section Badge */}
            {quote.section?.heading && (
              <div className="inline-flex items-center gap-2">
                <span className={cn(
                  'px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-wider',
                  'border backdrop-blur-sm',
                  style.badge
                )}>
                  {quote.section.heading}
                </span>
              </div>
            )}

            {/* Heading Quote */}
            {quote.headingQuote && (
              <h2 className={cn(
                'text-3xl sm:text-4xl font-bold leading-tight',
                style.text
              )}>
                {emoji ? cleanText : quote.headingQuote}
              </h2>
            )}

            {/* Sub Quote Text */}
            {quote.subQuoteText && (
              <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed">
                {quote.subQuoteText}
              </p>
            )}

            {/* Quote Text */}
            {quote.quoteText && !quote.headingQuote && (
              <div className="space-y-4">
                <p className={cn(
                  'text-xl sm:text-2xl font-medium leading-relaxed',
                  style.text
                )}>
                  "{emoji ? cleanText : quote.quoteText}"
                </p>
              </div>
            )}

            {/* Author */}
            {quote.author && (
              <p className="text-sm font-medium uppercase tracking-wider text-muted-foreground">
                — {quote.author}
              </p>
            )}

            {/* Continue Button */}
            <div className="pt-6">
              <Button 
                variant="primary" 
                size="lg" 
                onClick={onContinue}
                className="min-w-[160px] px-8 py-6 text-base font-semibold shadow-lg hover:shadow-xl transition-all"
              >
                Continue →
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
